import React, { useEffect, useRef, useState } from "react"; // SECURITY: added useRef, useEffect
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import useLogin from "../../features/auth/useLogin";

const LoginForm = () => {
  const navigate = useNavigate();

  const loginMutation = useLogin();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // SECURITY: refs to manage focus safely after failures
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // SECURITY: local brute-force throttle (client-side)
  const MAX_ATTEMPTS = 5;
  const LOCK_MINUTES = 5;
  const [attempts, setAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState(null);

  // SECURITY: sanitize helper – trims + removes control characters
  const sanitizeInput = (v) => v.replace(/[^\x20-\x7E]/g, "").trim();

  // SECURITY: stronger email validator (basic regex + length cap)
const validateEmail = (email) => {
    // Check for length limit (RFC standard)
    if (email.length > 254) {
        return false;
    }
    
    // Check for double at symbols
    if (email.includes('@@')) {
        return false;
    }
    
    // Check for double dots
    if (email.includes('..')) {
        return false;
    }
    
    // Regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};


  const validatePassword = (pwd) => pwd.length >= 8 && pwd.length <= 128;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const clean = sanitizeInput(value);
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // SECURITY: double-submit guard + throttle checks
  const newHandleLogin = async () => {
    // Prevent accidental double click while pending
    if (loginMutation.isPending) return;

    // Throttle check
    if (lockUntil && Date.now() < lockUntil) {
      const secs = Math.ceil((lockUntil - Date.now()) / 1000);
      setErrors((prev) => ({
        ...prev,
        submit: `Too many failed attempts. Try again in ${secs}s.`,
      }));
      return;
    }

    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password must be 8–128 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // SECURITY: focus the first invalid field
      if (newErrors.email && emailRef.current) emailRef.current.focus();
      else if (newErrors.password && passwordRef.current)
        passwordRef.current.focus();
      return;
    }

    // SECURITY: final sanitize before submission (defense in depth)
    const payload = {
      email: sanitizeInput(formData.email),
      password: sanitizeInput(formData.password),
    };

    // Note: not removing your code; just passing callbacks to track success/failure.
    loginMutation.mutate(payload, {
      onSuccess: () => {
        // SECURITY: reset lock and attempts on success
        setAttempts(0);
        setLockUntil(null);
      },
      onError: () => {
        // SECURITY: increment attempts and optionally lock
        const next = attempts + 1;
        setAttempts(next);
        if (next >= MAX_ATTEMPTS) {
          setLockUntil(Date.now() + LOCK_MINUTES * 60 * 1000);
        }
        // SECURITY: clear password on error to avoid lingering sensitive data
        setFormData((prev) => ({ ...prev, password: "" }));
        setShowPassword(false);
        if (passwordRef.current) passwordRef.current.focus();
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    newHandleLogin();
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  // SECURITY: watch mutation error state (in case hook uses global handlers)
  useEffect(() => {
    if (loginMutation.isError) {
      // keep error message generic; server should not leak detail
      setErrors((prev) => ({
        ...prev,
        submit: "Login failed. Please check your credentials and try again.",
      }));
    }
  }, [loginMutation.isError]);

  const buttonClass =
    "flex items-center justify-center rounded-lg py-2 transition duration-300";

  // SECURITY: compute remaining lock time for button text/aria
  const isLocked = lockUntil && Date.now() < lockUntil;
  const remainingSeconds = isLocked
    ? Math.ceil((lockUntil - Date.now()) / 1000)
    : 0;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="main"
      aria-labelledby="login-title"
    >
      <div
        className="absolute top-5 left-5 cursor-pointer"
        onClick={() => navigate(-1)}
        role="button"
        aria-label="Go back to the previous page"
      >
        <img
          src={GLOBAL_IMAGES.LoginBackButton}
          alt="Back"
          className="w-10 h-10"
        />
      </div>
      <div
        className="bg-white bg-opacity-60 p-12 rounded-3xl shadow-lg w-full max-w-xl px-4"
        role="region"
        aria-labelledby="login-title"
        aria-describedby="login-description"
      >
        <h2
          id="login-title"
          className="text-3xl font-bold text-center text-gray-800 mb-6"
        >
          Log in
        </h2>
        <p id="login-description" className="text-center text-gray-600 mb-8">
          Login to save your kids' progress as they work through courses on the
          <span className="font-bold text-indigo-600"> MATHDINGO</span>{" "}
          application
        </p>
        <form className="flex flex-col space-y-4" onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              className="block text-gray-700 font-bold mb-2"
              htmlFor="email"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              ref={emailRef} // SECURITY: focus management
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter Email Address"
              // SECURITY: safer input hints
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              maxLength={254} // SECURITY: hard cap typical email length
            />
            {errors.email && (
              <div className="flex items-center gap-2 mt-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              className="block text-gray-700 font-bold mb-2"
              htmlFor="password"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                ref={passwordRef} // SECURITY: focus management
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Password"
                // SECURITY: safer input hints
                autoComplete="current-password"
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                maxLength={128} // SECURITY: cap password length to prevent abuse/DoS on server
                enterKeyHint="done"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <div className="flex items-center gap-2 mt-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                <span>{errors.password}</span>
              </div>
            )}
          </div>

          <div className="text-right mb-4">
            <button
              onClick={handleForgotPassword}
              className="text-blue-800 text-sm font-semibold hover:underline"
              aria-label="Reset your password"
              type="button" // SECURITY: ensure this is not treated as submit by browsers
            >
              Forgot password?
            </button>
          </div>

          <button
            disabled={loginMutation.isPending || isLocked}
            className={`${buttonClass} bg-blue-800 text-white hover:bg-blue-900 w-full ${
              isLocked ? "opacity-60 cursor-not-allowed" : ""
            }`}
            aria-label={
              isLocked
                ? `Locked. Try again in ${remainingSeconds}s.`
                : "Login to your account"
            }
            type="submit"
          >
            {loginMutation.isPending ? (
              <Loader2 className="animate-spin mr-2" size={20} />
            ) : null}
            {isLocked ? `Locked (${remainingSeconds}s)` : "Login"}
          </button>

          {/* <div className="text-center text-black mt-6">
            <div className="border-t border-black my-4"></div>
            <p className="text-black-600 md:font-bold">New user?</p>
            <button
              onClick={() => navigate("/createaccountform")}
              className={`${buttonClass} bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 w-full`}
              aria-label="Create a new account"
            >
              Sign up
            </button>
          </div> */}

          {errors.submit && (
            <div className="flex items-center gap-2 mt-4 px-4 py-2 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="flex justify-between mt-4">
            <Link
              to="/subscription-terms"
              state={{ public: true }}
              className="text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "center" }}
              aria-label="Read subscription use"
            >
              Read Subscription Use
            </Link>
            <Link
              to={"/contact"}
              state={{ public: true }}
              className="text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "center" }}
              aria-label="Contact us for support"
            >
              Contact Us
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
